/**
 * The sign-in sequence, without React, so that it can be tested on its own:
 *
 * 1. Fetch a nonce from the API.
 * 2. Build a Sign-In with Ethereum message (EIP-4361) naming this site, the chain and the nonce.
 * 3. Have the wallet sign it.
 * 4. Send message and signature to the API, which checks them and opens a session.
 *
 * The API checks every field of the message against its own settings, so the message must be
 * built exactly as below.
 */

import { type Address, BaseError, type Hex, UserRejectedRequestError } from "viem";
import { createSiweMessage } from "viem/siwe";
import { ProviderNotFoundError } from "wagmi";

import { ApiRequestError, api } from "../api/client.js";

/** A signed-in wallet, as the API describes it. */
export interface Session {
  /** Wallet address in lowercase hex. */
  wallet: Address;
  isAdmin: boolean;
  /** ISO 8601 time at which the session ends. */
  expiresAt: string;
}

/** The statement wallets show to the user above the message details. */
export const SIGN_IN_STATEMENT = "Sign in to Morcast.";

/**
 * Builds the sign-in message.
 *
 * @param origin The app's origin, e.g. `https://app.morcast.xyz`. Its host is the message's
 *               domain, which wallets compare with the site asking for the signature.
 */
export function buildSignInMessage(input: {
  address: Address;
  chainId: number;
  nonce: string;
  origin: string;
  issuedAt?: Date;
}): string {
  const site = new URL(input.origin);
  return createSiweMessage({
    domain: site.host,
    address: input.address,
    statement: SIGN_IN_STATEMENT,
    uri: site.origin,
    version: "1",
    chainId: input.chainId,
    nonce: input.nonce,
    issuedAt: input.issuedAt ?? new Date(),
  });
}

/**
 * Runs the sign-in sequence.
 *
 * @param signMessage Asks the wallet to sign a message with `address`.
 * @returns The new session.
 */
export async function signIn(input: {
  address: Address;
  chainId: number;
  origin: string;
  signMessage: (message: string) => Promise<Hex>;
}): Promise<Session> {
  const { nonce } = await api.post<{ nonce: string }>("/api/auth/nonce");
  const message = buildSignInMessage({ ...input, nonce });
  const signature = await input.signMessage(message);
  return api.post<Session>("/api/auth/sign-in", { message, signature });
}

/** A short explanation of why signing in failed, for the user. */
export function describeSignInError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  if (isUserRejection(error)) {
    return "The request was cancelled in your wallet.";
  }
  if (error instanceof ProviderNotFoundError) {
    return "No browser wallet was found. Install a wallet extension, or choose Base Account.";
  }
  return "Signing in failed. Please try again.";
}

/** Whether the user declined a request in their wallet (EIP-1193 error 4001). */
function isUserRejection(error: unknown): boolean {
  if (error instanceof BaseError) {
    return error.walk((cause) => cause instanceof UserRejectedRequestError) !== null;
  }
  return (error as { code?: unknown } | null)?.code === 4001;
}
